import { createMDAiReportLink } from '../api/createMDAiReportLink';
import { useGlobalStore } from '@rs-core/store/globalStore';
import { logInfo, logError } from '@rs-core/utils/logger';
import { useConfig } from '@rs-core/context/ConfigContext';
import ImagingStudyWorklistMapping from '@rs-core/fhir/resource/columnMapping/ImagingStudyWorklistMapping';
import { setLocalStorageItem, getLocalStorageItem } from '../utils/localStorageUtil';
import { calculateExpiryTime, getTimeDifference } from '../utils/time';
import { MD_AI_REPORT_CREATION_PROGRESS } from '@rs-core/store/slices/aiReportingIntegrationSlice';
import { useAuth } from '@rs-core/context/UserAuthContext';

// Constants
const LOCAL_STORAGE_KEY = 'mdAiDraftReportData';
const EXPIRY_TIME_IN_MINUTES = 50;

const useCreateMDAiReportLink = () => {
	const __config = useConfig();
	const { selectedLanguage } = useAuth();
	const currentStudy = useGlobalStore(state => state.currentStudyInfo);
	const { setMdAiInitialReportCreationData, setMdAiReportCreationProgress, mdAiReportCreationProgress } =
		useGlobalStore(state => ({
			setMdAiInitialReportCreationData: state.aiReportingIntegration.mdAi.setMdAiInitialReportCreationData,
			setMdAiReportCreationProgress: state.aiReportingIntegration.mdAi.setMdAiReportCreationProgress,
			mdAiReportCreationProgress: state.aiReportingIntegration.mdAi.mdAiReportCreationProgress,
		}));

	// Helper functions
	const generateAccessionSuffix = () => {
		const suffix = Date.now().toString().slice(-6);
		logInfo('useCreateMDAiReportLink', 'Generated accession suffix', { suffix });
		return suffix;
	};

	const isReportExpired = expiryTime => {
		const isExpired = Date.now() >= expiryTime;
		logInfo('useCreateMDAiReportLink', 'Checking report expiry', {
			isExpired,
			currentTime: Date.now(),
			expiryTime,
			timeLeftMs: expiryTime - Date.now(),
		});
		return isExpired;
	};

	// Generate minimal payload for expired report refresh
	const generateMinimalPayload = (existingAccession, study = currentStudy) => ({
		clinicalInfo: {
			PatientMRN: study?.patientID || '',
			Accession: existingAccession,
		},
		reportingApp: {
			AppLanguage: selectedLanguage,
			SpeechLanguage: selectedLanguage,
			ReturnFormat: 'HL7',
			SyncLaunch: true,
		},
		response: { type: 'all' },
	});

	// Generate full payload for new report
	const generateFullPayload = (study = currentStudy) => {
		try {
			const { subject, patientID, birthDate, gender } = study;
			const { extension } = study;

			// Safely extract StudyExtJson
			const studyExtJsonExt = extension?.find(ele => ele.url === 'StudyExtJson');
			if (!studyExtJsonExt?.valueString) {
				throw new Error('StudyExtJson not found in study extension');
			}

			// Safely parse JSON string

			const studyExtJson = JSON.parse(studyExtJsonExt.valueString);

			// Safely extract values with null checks
			const { modality = [], bodypart = [], laterality = [] } = studyExtJson || {};

			// Safely create strings with proper null checks and data validation
			const modalityString = Array.isArray(modality)
				? modality
						.filter(m => m?.code)
						.map(m => m.code)
						.join(',')
				: '';

			const bodyPartString = Array.isArray(bodypart)
				? bodypart
						.filter(b => b?.display)
						.map(b => b.display)
						.join(',')
				: '';

			const lateralityString = Array.isArray(laterality)
				? laterality
						.filter(l => l?.display)
						.map(l => l.display)
						.join(',')
				: '';

			const studyAccessionNumber = ImagingStudyWorklistMapping.accessionNumber.getDisplay(study) || '';
			const generatedAccession = `${studyAccessionNumber}-${generateAccessionSuffix()}`;

			// Create a clean payload object with primitive values only
			const payload = {
				clinicalInfo: {
					PatientMRN: patientID || '',
					Accession: generatedAccession,
					PatientName: subject?.display || '',
					PatientBirthDate: birthDate || '',
					PatientAge: '',
					PatientSex: gender || '',
					Modality: modalityString,
					BodyPartExamined: bodyPartString,
					Laterality: lateralityString,
					StudyDescription: study?.description || '',
					StudyDate: '',
					StudyTime: '',
					StudyInstanceUID: '',
					ReasonForExam: '',
					KeyFindings: '',
					ReferringPhysician: '',
					ReportingPhysician: '',
					Notes: '',
				},
				reportingApp: {
					AppLanguage: selectedLanguage,
					SpeechLanguage: selectedLanguage,
					ReturnFormat: 'HL7',
					SyncLaunch: true,
				},
				response: { type: 'all' },
			};

			return payload;
		} catch (error) {
			logError('useCreateMDAiReportLink', 'Failed to generate payload', {
				error: error.message,
				studyId: study?.id,
			});
			throw error;
		}
	};

	// Update report state and storage
	const updateReportState = (reportData, draftData, study = currentStudy) => {
		logInfo('useCreateMDAiReportLink', 'Updating report state', {
			accessionNumber: reportData.accessionNumber,
			expiryTime: reportData.expiryTime,
			studyId: study?.id,
		});

		setMdAiInitialReportCreationData(reportData);
		setMdAiReportCreationProgress(MD_AI_REPORT_CREATION_PROGRESS.COMPLETED);

		setLocalStorageItem(LOCAL_STORAGE_KEY, {
			...draftData,
			[study?.id]: reportData,
		});

		logInfo('useCreateMDAiReportLink', 'Report state update completed', {
			status: MD_AI_REPORT_CREATION_PROGRESS.COMPLETED,
		});
	};

	// Handle expired report refresh
	const refreshExpiredReport = async (storedReport, draftData, study = currentStudy) => {
		logInfo('useCreateMDAiReportLink', 'Starting expired report refresh', {
			oldAccessionNumber: storedReport.accessionNumber,
			oldExpiryTime: storedReport.expiryTime,
		});

		const minimalPayload = generateMinimalPayload(storedReport.accessionNumber, study);

		logInfo('useCreateMDAiReportLink', 'Calling MD.ai API for refresh');
		const reFetchedReportData = await createMDAiReportLink({ ...minimalPayload, _config: __config });

		const newExpiryTime = Date.now() + EXPIRY_TIME_IN_MINUTES * 60 * 1000;
		logInfo('useCreateMDAiReportLink', 'Calculated new expiry time', {
			newExpiryTime,
			expiresInMinutes: EXPIRY_TIME_IN_MINUTES,
		});

		const updatedMdAiReportData = {
			...reFetchedReportData,
			accessionNumber: storedReport.accessionNumber,
			expiryTime: newExpiryTime,
		};

		updateReportState(updatedMdAiReportData, draftData, study);
	};

	// Handle existing report logic
	const handleExistingReport = async (storedReport, draftData, study = currentStudy) => {
		// draftData passed through to updateReportState to preserve other drafts
		if (isReportExpired(storedReport.expiryTime)) {
			await refreshExpiredReport(storedReport, draftData, study);
			return;
		}

		const { minutes, seconds } = getTimeDifference(storedReport.expiryTime);
		logInfo('useCreateMDAiReportLink', `Using existing report (expires in ${minutes}m ${seconds}s)`);

		setMdAiInitialReportCreationData(storedReport);
		setMdAiReportCreationProgress(MD_AI_REPORT_CREATION_PROGRESS.COMPLETED);
	};

	// Create new report
	const createNewReport = async (draftData, study = currentStudy) => {
		logInfo('useCreateMDAiReportLink', 'Starting new report creation', {
			studyId: study?.id,
		});

		const payload = generateFullPayload(study);
		logInfo('useCreateMDAiReportLink', 'Generated payload for new report', {
			accessionNumber: payload.clinicalInfo.Accession,
			patientMRN: payload.clinicalInfo.PatientMRN,
			modality: payload.clinicalInfo.Modality,
			bodyPart: payload.clinicalInfo.BodyPartExamined,
			laterality: payload.clinicalInfo.Laterality,
		});

		logInfo('useCreateMDAiReportLink', 'Calling MD.ai API for new report');
		const newReportData = await createMDAiReportLink({ ...payload, _config: __config });

		const newMdAiReportData = {
			...newReportData,
			accessionNumber: payload.clinicalInfo.Accession,
			expiryTime: calculateExpiryTime(EXPIRY_TIME_IN_MINUTES),
		};

		updateReportState(newMdAiReportData, draftData, study);
	};

	// Main report creation function
	const createMdAiReport = async (record = null) => {
		const studyToUse = record || currentStudy;

		logInfo('useCreateMDAiReportLink', 'Starting report creation process', {
			currentProgress: mdAiReportCreationProgress,
			studyId: studyToUse?.id,
		});

		if (mdAiReportCreationProgress === MD_AI_REPORT_CREATION_PROGRESS.IN_PROGRESS) {
			logInfo('useCreateMDAiReportLink', 'Report creation already in progress, aborting');
			return;
		}

		setMdAiReportCreationProgress(MD_AI_REPORT_CREATION_PROGRESS.IN_PROGRESS);

		try {
			if (!studyToUse) {
				logError('useCreateMDAiReportLink', 'No study data available');
				throw new Error('No study data available');
			}

			const draftData = getLocalStorageItem(LOCAL_STORAGE_KEY) || {};
			const storedReport = draftData[studyToUse?.id];

			logInfo('useCreateMDAiReportLink', 'Checked local storage for existing report', {
				hasStoredReport: !!storedReport,
				studyId: studyToUse?.id,
				storedReportAccession: storedReport?.accessionNumber,
			});

			if (storedReport && storedReport?.status === 'Draft') {
				await handleExistingReport(storedReport, draftData, studyToUse);
				return;
			}

			// Pass entire draftData to maintain other drafts
			await createNewReport(draftData, studyToUse);
		} catch (error) {
			logInfo('useCreateMDAiReportLink', 'Report creation failed', {
				error: error.message,
				studyId: studyToUse?.id,
				errorCode: error.code,
				errorStack: error.stack,
			});
			setMdAiReportCreationProgress(MD_AI_REPORT_CREATION_PROGRESS.FAILED);
			throw error;
		}
	};

	return { createMdAiReport };
};

export default useCreateMDAiReportLink;
